import { bookRequested, erasPages, type SagaBook } from "../witness/saga";
import type { Hud } from "./hud";
import type { ErasPage, ErasView } from "./panels";

/*
 * How a session begins: the world's own history, read before anything else.
 *
 * Phase 6 plan, wave 2C. The phase's opening image is *"the game opens with the
 * player reading the world's actual history in a book that exists on shelves"*,
 * and this is the whole of that sentence's first half — the world is built, the
 * Book of Eras is put in front of the player, and the ordinary session starts
 * when they close it.
 *
 * Four properties are load-bearing and each is a rule rather than a taste:
 *
 * - **It is skippable at any point.** Enter, Escape, the button, or a click
 *   anywhere off the page. The name card's precedent (`hud/panels.ts`'s
 *   `NamePrompt`): a first-run moment a player cannot leave is a first-run
 *   moment they resent, and *"or don't"* is the standing answer to every one of
 *   them this client puts up.
 * - **It is gated on the URL alone** (`witness/saga.ts`'s `bookRequested`), never
 *   on `import.meta.env.DEV` — O7's rule. `?book=0` is the same session without
 *   it, which is what makes night-start against book-start one playtest instead
 *   of two builds.
 * - **It runs before the loop and before the controls exist**, which is what
 *   makes the keyboard handling below safe to write at all: nothing else is
 *   listening yet, so Enter cannot also be a jump and Escape cannot also drop a
 *   pointer lock that has never been taken (`main.ts` constructs `Controls`
 *   after this resolves).
 * - **Nothing on the page is composed here.** The ages, their names and their
 *   lines are the simulation's; this file decides which page is open and what
 *   the foot says about turning them.
 *
 * **Since chunk U-4 the way out of the book is the way in to the lives.** There
 * is no bridge screen between them any more — the book closes and the strip of
 * lives is already on the same veil, with the question the bridge used to hold
 * said quietly over it (`hud/origins.ts`). Nothing in this file had to move for
 * that: the page loses its frame and reads on the dark, which is one variant in
 * `ui/reading.css`, and what `onBegin` resolves into is the caller's business.
 */

/** What the book is called, on the page. The plan's own name for it (decision O6). */
const OPENING_TITLE = "the book of eras";

/**
 * The foot line: how to turn a page, and how to leave.
 *
 * Both ways out are named because they are different gestures for the same
 * thing, and a player who has decided they are done should not have to find the
 * button — the same reasoning the name card's two plain buttons are under.
 *
 * **One clause since chunk U-4, and the bridge's death is why.** It used to read
 * *"enter begins · escape closes the book"*, which named two outcomes for one
 * gesture: enter began a session and escape shut a book. They were always the
 * same press and the sequence makes that plain — the book closes and the lives
 * are already there — so the line says it once. It deliberately does not promise
 * *lives*: `?born=0` and a world with none go on to the naming instead, and a
 * foot line that named the next surface would be wrong in both
 * (`hud/origins.ts`).
 */
const OPENING_HINT = "← → turn the pages · enter or escape closes the book";

/**
 * What happened at the start of a session, for a driver and for the record.
 *
 * Three outcomes rather than a boolean because they are three different worlds:
 * the link declined the book, the world had no history to write one, or somebody
 * read it. A self-check that could not tell the second from the first would pass
 * on a build whose saga export had quietly stopped answering.
 */
export type OpeningRead = "declined" | "unwritten" | "read";

/**
 * Puts the Book of Eras in front of the player and resolves when they close it.
 *
 * The awaited call in `main.ts`'s boot sequence, between the world being built
 * and the controls being armed. **Everything asynchronous about it is the wait
 * for a person**: the book is handed in already read — the witnessing layer
 * pulls it on the day boundary like the rest of the bulk (`witness/witness.ts`)
 * — so nothing here blocks a frame, and the loading prompt this client has been
 * showing for the last couple of seconds is still painted when the page arrives
 * to replace it.
 *
 * `null` is a session with nothing to witness (`?bootstrap=0`), which reads the
 * same way as a world with no ages: no book, no delay, no comment.
 */
export async function openTheBook(
  hud: Hud,
  book: SagaBook | null,
  search: string,
): Promise<OpeningRead> {
  if (!bookRequested(search)) {
    return "declined";
  }

  const pages = book === null ? [] : erasPages(book);

  if (pages.length === 0) {
    // A world with no age worth an age's name — `?bootstrap=0`, or a boundary
    // that does not answer with a saga yet. Nothing is said about it: an empty
    // book held up for a second would be a worse start than no book at all.
    return "unwritten";
  }

  // The loading prompt is what is on screen when the book arrives, and the book
  // is what replaces it. The play prompt goes up after this resolves, which is
  // `main.ts`'s business and stays there.
  hud.setOverlay(null);

  await turnThrough(hud, pages);

  return "read";
}

/**
 * Draws the page, listens for the four gestures, and resolves when the reader is
 * done.
 *
 * The listener is on `document` rather than on the panel because the panel is
 * not focusable and a book is not a form: what a reader presses should work
 * wherever the cursor happens to be. It is removed the moment the book closes,
 * so nothing this file bound survives into the session that follows — which
 * matters more than usual here, since `Controls` binds the same keys on the same
 * target a few lines later (`input/controls.ts`).
 */
async function turnThrough(hud: Hud, pages: readonly ErasPage[]): Promise<void> {
  await new Promise<void>((resolve) => {
    let page = 0;
    let done = false;

    const close = (): void => {
      if (done) {
        return;
      }

      done = true;
      document.removeEventListener("keydown", onKey);
      hud.eras.hide();
      resolve();
    };

    const draw = (): void => {
      hud.eras.show({
        where: "opening",
        title: OPENING_TITLE,
        pages,
        page,
        hint: OPENING_HINT,
        onTurn: (wanted: number) => {
          // Clamped here as well as in the panel, so the two never disagree
          // about which page is open: the panel clamps what it draws, and this
          // clamps what the *next* press turns from.
          page = Math.min(Math.max(wanted, 0), pages.length - 1);
          draw();
        },
        onBegin: close,
      } satisfies ErasView);
    };

    const onKey = (event: KeyboardEvent): void => {
      // **A digit or a letter typed into the one text field in this client is
      // part of the name** — the lab's rule, stated against every key handler the
      // opening's flow owns (`ui-lab/surfaces/opening.html`, `hud/origins.ts`).
      // Nothing can reach here from the field today (it is not up while the book
      // is, and it stops its own events anyway), and the guard is written all the
      // same: the digits stop being free in this wave (chunk U-5's hotbar), and a
      // rule that only holds because of what the boot order happens to be is a
      // rule the next reordering silently breaks.
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      if (event.key === "Enter" || event.key === "Escape") {
        event.preventDefault();
        close();

        return;
      }

      const step = turnFor(event.key);

      if (step === 0) {
        return;
      }

      event.preventDefault();

      const wanted = page + step;

      // A turn off either end of the book is nothing rather than the way out.
      // The page a reader is on is the page they meant to be on, and a book that
      // ejected them for pressing the same key once more would be a book that
      // punished reading it to the end.
      if (wanted < 0 || wanted >= pages.length) {
        return;
      }

      page = wanted;
      draw();
    };

    document.addEventListener("keydown", onKey);
    draw();
  });
}

/**
 * Which way a key turns the page: forward, back, or not at all.
 *
 * By `key` rather than by `code`, unlike everything in `input/controls.ts`, and
 * the difference is what each is for: that file binds *letters* to verbs and has
 * to survive a layout, and these are the keys that already mean "next" and
 * "previous" on every keyboard whatever is printed on them.
 */
function turnFor(key: string): number {
  if (key === "ArrowRight" || key === "ArrowDown" || key === "PageDown" || key === " ") {
    return 1;
  }

  if (key === "ArrowLeft" || key === "ArrowUp" || key === "PageUp") {
    return -1;
  }

  return 0;
}
